import { defineStore } from 'pinia'
import type { CartItem } from '~/types/item'
import { shippingBenefitBase, isShippingBenefitReached, type ShippingBenefit } from '~/lib/shippingBenefit'

const REFRESH_MS = 5 * 60 * 1000

/**
 * Shipping benefit (toimitusetu): the active rule, and which cart products count towards its threshold.
 * Payment validation (functions) decides; the front shows the same result in advance.
 */
export const useShippingBenefitStore = defineStore('shippingBenefit', {
  state: () => ({
    benefit: null as ShippingBenefit | null,
    fetchedAt: 0,
    /** Cart product skus that match the rule's product filter; null = the rule has no filter (all count) */
    matchingSkus: null as string[] | null
  }),
  getters: {
    /** Products sum after discounts that counts towards the threshold */
    base(): number {
      const cartStore = useCartStore()
      const matching = this.matchingSkus
      return shippingBenefitBase(cartStore.items, cartStore.campaign,
        (item: CartItem) => matching === null || item.sparePart === true || matching.includes(item.sku))
    },
    isReached(): boolean {
      return isShippingBenefitReached(this.benefit, this.base, {
        isBusinessCustomer: useCheckoutStore().isBusinessCustomer,
        isLoggedIn: useUserStore().loggedIn
      })
    },
    remaining(): number {
      return this.benefit ? Math.max(Math.round((this.benefit.threshold - this.base) * 100) / 100, 0) : 0
    },
    freeDeliveryMethodIds(): number[] {
      return this.isReached && this.benefit ? this.benefit.deliveryMethodIds : []
    }
  },
  actions: {
    /** The active rule; any error means no benefit (the customer then sees normal prices) */
    async fetchShippingBenefit(force = false) {
      if (!force && Date.now() - this.fetchedAt < REFRESH_MS) return
      this.fetchedAt = Date.now()
      try {
        const response = await $fetch<{ active: ShippingBenefit[] }>(useRuntimeConfig().public.FUNCTIONS_API_URL + '/tkapi-campaign-getShippingBenefitV2')
        this.benefit = response.active?.[0] || null
      }
      catch (error) {
        console.error('Shipping benefit fetch failed', error)
        this.benefit = null
      }
      await this.refreshMatchingSkus()
    },
    async refreshMatchingSkus() {
      const filter = this.benefit?.productFilter
      if (!filter) {
        this.matchingSkus = null
        return
      }
      const skus = useCartStore().items.filter(item => !item.sparePart).map(item => item.sku)
      if (skus.length === 0) {
        this.matchingSkus = []
        return
      }
      try {
        const { search } = useSearch()
        const quoted = skus.map(sku => '`' + sku.replace(/`/g, '') + '`').join(',')
        const result = await search({ query: '', requestOptions: { filters: `(${filter}) && sku:=[${quoted}]`, attributesToRetrieve: ['sku'], attributesToHighlight: [], hitsPerPage: skus.length } })
        this.matchingSkus = (result?.hits || []).map((hit: { sku: string }) => hit.sku)
      }
      catch (error) {
        console.error('Shipping benefit product filter failed', error)
        this.matchingSkus = []
      }
    }
  }
})
